// The whole workspace as one graph.
//
// The old Impact view drew a single kind of edge (work @mentioning work) in a left-to-
// right lineage layout, which encoded a claim the data doesn't make: that influence
// flows in one direction along one axis. It doesn't. A demo lands a deal, the deal
// pays for a deliverable, the deliverable reuses the demo's renderer, the person who
// saw the demo moves to another org and asks for it there.
//
// So this builds every edge the log actually contains, across all four record kinds,
// and hands them to a force layout that lets the shape emerge instead of asserting one.
// Nothing here is stored: every edge is derived, which is why there's no relationship
// editor to keep in sync.

import { mentionedIds } from './mentions'
import { KINDS, type EntityKind } from './model'
import { fieldStr, entitiesOfKind, buildsOn, type EntityRecord, type State } from './reducer'

/** Why two records are connected. The kind drives colour and weight in the drawing. */
export type EdgeKind =
  | 'party' // deal ↔ org, person ↔ org: who is involved
  | 'work' // deal ↔ the work associated with it
  | 'builds' // work → work it builds on
  | 'renews' // deal → deal it continues
  | 'owns' // person → work they lead
  | 'mention' // anything → anything, via prose

export interface NetNode {
  id: string
  kind: EntityKind
  label: string
  /** how connected it is, which is what "load-bearing" actually means here */
  degree: number
}

export interface NetEdge {
  from: string
  to: string
  kind: EdgeKind
}

export interface Network {
  nodes: NetNode[]
  edges: NetEdge[]
}

/** Edge kinds in drawing order: structure first, prose last. */
export const EDGE_ORDER: EdgeKind[] = ['party', 'work', 'builds', 'renews', 'owns', 'mention']

export const EDGE_LABEL: Record<EdgeKind, string> = {
  party: 'involved',
  work: 'work on',
  builds: 'builds on',
  renews: 'renews',
  owns: 'leads',
  mention: 'mentioned',
}

/** Which accent an edge takes: money edges ember, work edges marine, prose neutral. */
export const EDGE_HUE: Record<EdgeKind, 'money' | 'work' | 'neutral'> = {
  party: 'money',
  work: 'work',
  builds: 'work',
  renews: 'money',
  owns: 'work',
  mention: 'neutral',
}

/**
 * The prose that can carry @mentions. `description` is the field every kind uses now;
 * the rest are what it replaced, and old logs still have mentions in them.
 */
const PROSE = ['description', 'context', 'summary', 'about']

export function buildNetwork(state: State): Network {
  const nodes = new Map<string, NetNode>()
  for (const kind of Object.keys(KINDS) as EntityKind[]) {
    for (const rec of entitiesOfKind(state, kind)) {
      nodes.set(rec.id, {
        id: rec.id,
        kind,
        label: titleFor(rec),
        degree: 0,
      })
    }
  }

  const edges: NetEdge[] = []
  const seen = new Set<string>()
  const add = (from: string, to: string, kind: EdgeKind) => {
    if (!from || !to || from === to) return
    if (!nodes.has(from) || !nodes.has(to)) return
    // One edge per pair per kind, in whichever direction it was first seen: the layout
    // treats them as springs, so a duplicated pair would just pull twice as hard.
    const key = [from, to].sort().join('~') + kind
    if (seen.has(key)) return
    seen.add(key)
    edges.push({ from, to, kind })
  }

  for (const deal of entitiesOfKind(state, 'deal')) {
    add(deal.id, fieldStr(deal, 'org'), 'party')
    for (const to of deal.links) add(deal.id, to, 'party')
    add(deal.id, fieldStr(deal, 'renews'), 'renews')
  }

  for (const person of entitiesOfKind(state, 'person')) {
    for (const to of person.links) add(person.id, to, 'party')
  }

  for (const work of entitiesOfKind(state, 'project')) {
    for (const to of work.links) add(work.id, to, 'party')
    add(fieldStr(work, 'owner'), work.id, 'owns')
    // Lineage, still: it's one edge kind among several now rather than the whole graph.
    for (const to of buildsOn(state, work.id)) add(work.id, to, 'builds')
  }

  for (const s of Object.values(state.support)) add(s.deal, s.project, 'work')
  // A commitment pointing at a work is a real deal ↔ work connection even when nobody
  // attached it in the Works list, so the graph reads it too. `add` dedupes the overlap.
  for (const c of Object.values(state.commitments)) {
    if (!c.archived) add(c.deal, c.work, 'work')
  }

  // Prose. An @mention in a description or an entry is a real connection someone made
  // by hand, and it's often the only thing joining two records the schema can't.
  for (const rec of Object.values(state.entities)) {
    if (rec.archived) continue
    for (const key of PROSE) {
      for (const to of mentionedIds(fieldStr(rec, key))) add(rec.id, to, 'mention')
    }
  }
  for (const entry of Object.values(state.entries)) {
    if (entry.archived) continue
    for (const to of mentionedIds(entry.body)) add(entry.subject, to, 'mention')
  }

  for (const e of edges) {
    const a = nodes.get(e.from)
    const b = nodes.get(e.to)
    if (a) a.degree += 1
    if (b) b.degree += 1
  }

  return { nodes: [...nodes.values()], edges }
}

function titleFor(rec: EntityRecord): string {
  const field = KINDS[rec.kind].fields.find((f) => f.title)?.key ?? 'name'
  return fieldStr(rec, field) || `Untitled ${KINDS[rec.kind].singular.toLowerCase()}`
}

/** Drop the records nothing connects to: an isolated dot answers no question. */
export function connectedOnly(net: Network): Network {
  const keep = new Set<string>()
  for (const e of net.edges) {
    keep.add(e.from)
    keep.add(e.to)
  }
  return { nodes: net.nodes.filter((n) => keep.has(n.id)), edges: net.edges }
}

/** Narrow the graph to one kind's neighbourhood, keeping the edges between what's left. */
export function filterKinds(net: Network, kinds: EntityKind[]): Network {
  if (kinds.length === 0) return net
  const set = new Set(kinds)
  const nodes = net.nodes.filter((n) => set.has(n.kind))
  const ids = new Set(nodes.map((n) => n.id))
  return { nodes, edges: net.edges.filter((e) => ids.has(e.from) && ids.has(e.to)) }
}

/**
 * One record's neighbourhood: itself, whatever it touches, and (at depth 2) whatever
 * those touch. This is the graph a detail page wants — the whole workspace around a
 * single node is noise, but its immediate surroundings are the answer to "what is this
 * connected to".
 */
export function neighbourhood(net: Network, id: string, depth = 2): Network {
  const keep = new Set([id])
  let frontier = [id]
  for (let step = 0; step < depth; step++) {
    const next: string[] = []
    for (const e of net.edges) {
      if (frontier.includes(e.from) && !keep.has(e.to)) {
        keep.add(e.to)
        next.push(e.to)
      }
      if (frontier.includes(e.to) && !keep.has(e.from)) {
        keep.add(e.from)
        next.push(e.from)
      }
    }
    if (next.length === 0) break
    frontier = next
  }
  return {
    nodes: net.nodes.filter((n) => keep.has(n.id)),
    edges: net.edges.filter((e) => keep.has(e.from) && keep.has(e.to)),
  }
}

// ── the ecosystem's bands ────────────────────────────────────────────────────
//
// The other drawing of the same log. `buildNetwork` above hands a force layout
// every edge and lets the shape emerge; the ecosystem view draws ONE edge kind —
// work claiming work as an ancestor — and asserts a direction for it, because
// there the direction is the whole point. Vertical position is the claim, so the
// bands have to be worked out before anything is drawn.
//
// Two ways to band the same edges, and they are the two questions being asked:
// one client's page asks "what does THEIR work sit between", the corpus asks
// "how deep does the lineage go".

/** One row of the banded drawing, top to bottom. */
export interface EcoBand {
  key: string
  /** the small mono label at the left of the row */
  label: string
  ids: string[]
}

/** The ordered-pair edges an ecosystem drawing is built from. */
interface Ancestry {
  from: string
  to: string
}

/**
 * One client's three bands: what their work builds on, their work, what it feeds.
 *
 * A record can only be upstream or downstream of the middle, never both, so first
 * placement wins and the layout stays stable. Only records that actually appear in
 * a connection are placed — a web is about what is connected, and drawing every
 * project the client owns put unattached dots through the middle of it, which
 * crowds the band that matters and claims a place in the network for work that has
 * none.
 *
 * All three bands come back even when empty, so the middle row does not slide up
 * the page on a client whose work builds on nothing.
 */
export function clientBands(edges: Ancestry[], mine: Set<string>): EcoBand[] {
  const rows: Record<'up' | 'mine' | 'down', string[]> = { up: [], mine: [], down: [] }
  const seen = new Set<string>()
  const put = (id: string, band: 'up' | 'mine' | 'down') => {
    if (seen.has(id)) return
    seen.add(id)
    rows[band].push(id)
  }
  for (const e of edges) {
    if (mine.has(e.from)) put(e.from, 'mine')
    if (mine.has(e.to)) put(e.to, 'mine')
  }
  for (const e of edges) {
    if (mine.has(e.to)) put(e.from, 'up')
    else if (mine.has(e.from)) put(e.to, 'down')
  }
  return [
    { key: 'up', label: 'Builds on', ids: rows.up },
    { key: 'mine', label: 'Their work', ids: rows.mine },
    { key: 'down', label: 'What it feeds', ids: rows.down },
  ]
}

/**
 * The same drawing with no client at its centre: every connected record in the log,
 * banded by how far it sits from a root.
 *
 * A root is work whose description claims no ancestor here — the lineage starts
 * with it. Everything below is built on the band above it, by the LONGEST path
 * rather than the shortest: if a piece of work is both a direct descendant of a
 * root and four generations down another line, four is the honest depth, and the
 * short edge is then drawn as the skip it is.
 *
 * Prose can disagree with itself — two records each claiming the other as an
 * ancestor is a cycle, and a cycle has no depth. Those records are not dropped
 * (the shape of the web is the thing this view exists to show); each one is placed
 * just under the deepest ancestor that did get a depth, and the edge that closes
 * the loop is simply drawn going back up.
 */
export function corpusBands(edges: Ancestry[]): EcoBand[] {
  const ids = new Set<string>()
  for (const e of edges) {
    ids.add(e.from)
    ids.add(e.to)
  }

  const up = new Map<string, string[]>()
  const down = new Map<string, string[]>()
  const waiting = new Map<string, number>()
  for (const id of ids) {
    up.set(id, [])
    down.set(id, [])
    waiting.set(id, 0)
  }
  // One arrow per ordered pair. A pair repeated (the same ancestor claimed in two
  // sentences) would otherwise be counted twice and never reach zero.
  const pairs = new Set<string>()
  for (const e of edges) {
    if (e.from === e.to) continue
    const key = `${e.from}->${e.to}`
    if (pairs.has(key)) continue
    pairs.add(key)
    up.get(e.to)?.push(e.from)
    down.get(e.from)?.push(e.to)
    waiting.set(e.to, (waiting.get(e.to) ?? 0) + 1)
  }

  const depth = new Map<string, number>()
  const queue = [...ids].filter((id) => waiting.get(id) === 0)
  for (const id of queue) depth.set(id, 0)
  for (let i = 0; i < queue.length; i++) {
    const id = queue[i] as string
    for (const next of down.get(id) ?? []) {
      depth.set(next, Math.max(depth.get(next) ?? 0, (depth.get(id) ?? 0) + 1))
      const left = (waiting.get(next) ?? 0) - 1
      waiting.set(next, left)
      if (left === 0) queue.push(next)
    }
  }
  // Whatever a cycle swallowed. Placed under its deepest placed ancestor, which for
  // a record whose only ancestors are also in the cycle means the top band.
  for (const id of ids) {
    if (depth.has(id)) continue
    const above = (up.get(id) ?? []).map((p) => (depth.get(p) ?? -1) + 1)
    depth.set(id, Math.max(0, ...above))
  }

  const rows = new Map<number, string[]>()
  for (const id of ids) {
    const d = depth.get(id) ?? 0
    const row = rows.get(d)
    if (row) row.push(id)
    else rows.set(d, [id])
  }
  return [...rows.keys()]
    .sort((a, b) => a - b)
    .map((d) => ({
      key: `gen${d}`,
      // The top band is the only one that needs naming: everything under it is
      // built on what sits above, which the drawing already says.
      label: d === 0 ? 'Roots' : `Generation ${d + 1}`,
      ids: rows.get(d) ?? [],
    }))
}
